import { useEffect, useState, useMemo } from "react";
import { Icon, Avatar, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { fmtDateTime, fmtRelative } from "../../lib/dates.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import IpCell from "../../components/admin/IpCell.jsx";
import { listAuditLog, listAuditActors, itemsOf } from "../../lib/adminApi";

const PAGE_SIZE = 50;

const RANGES = [
  [7, "7 days"],
  [30, "30 days"],
  [90, "90 days"],
];

const ACTION_COLOR = {
  delete: "badge-error",
  suspend: "badge-error",
  cancel: "badge-error",
  refund_failed: "badge-error",
  refund_processed: "badge-info",
  create: "badge-success",
  grant: "badge-success",
  invite: "badge-success",
  update: "badge-info",
  change: "badge-info",
  extend: "badge-info",
  unsuspend: "badge-success",
  reactivate: "badge-success",
  retry_processed: "badge-success",
  retry_failed: "badge-error",
};

const actionColor = (a) => {
  if (!a) return "badge-muted";
  const lower = a.toLowerCase();
  for (const [k, v] of Object.entries(ACTION_COLOR)) {
    if (lower.includes(k)) return v;
  }
  return "badge-muted";
};

function AuditInner() {
  const { toast, openModal, closeModal } = useApp();
  const [resp, setResp] = useState({ items: [], total: 0, hasMore: false, page: 1 });
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);
  const [search, setSearch] = useState("");
  const [actor, setActor] = useState("");
  const [targetType, setTargetType] = useState("");
  const [actors, setActors] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    listAuditActors().then(setActors).catch(() => setActors([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (dateFrom || dateTo) {
      if (dateFrom) qs.set("from", dateFrom);
      if (dateTo) qs.set("to", dateTo);
    } else {
      qs.set("range", String(range));
    }
    if (actor) qs.set("actor", actor);
    if (targetType) qs.set("targetType", targetType);
    qs.set("page", String(page));
    qs.set("limit", String(PAGE_SIZE));
    listAuditLog(`?${qs}`)
      .then((d) => setResp(itemsOf(d)))
      .catch((e) => toast(e.message || "Failed to load audit log", "error"))
      .finally(() => setLoading(false));
  }, [range, dateFrom, dateTo, actor, targetType, page, toast]);

  useEffect(() => { setPage(1); }, [range, dateFrom, dateTo, actor, targetType, search]);

  const filtered = useMemo(() => resp.items.filter((l) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (l.action || "").toLowerCase().includes(q) ||
      (l.targetType || "").toLowerCase().includes(q) ||
      (l.actor?.email || "").toLowerCase().includes(q) ||
      (l.targetId || "").toLowerCase().includes(q)
    );
  }), [resp.items, search]);

  const totalPages = Math.max(1, Math.ceil(resp.total / PAGE_SIZE));

  const openMetadata = (l) => {
    const meta = l.metadata;
    const metaEntries = meta && typeof meta === "object" ? Object.entries(meta) : [];

    openModal(
      <>
        <div className="modal-head">
          <h3>
            <span className={`badge ${actionColor(l.action)}`} style={{ marginRight: 8 }}>{l.action}</span>
            Audit Entry
          </h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body" style={{ padding: 0 }}>
          <div style={{ padding: 20, borderBottom: "1px solid var(--border)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "10px 0", fontSize: 13 }}>
              <MetaLabel>Timestamp</MetaLabel>
              <MetaValue>{fmtDateTime(l.createdAt)}</MetaValue>

              <MetaLabel>Actor</MetaLabel>
              <MetaValue>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Avatar name={l.actor?.email || "?"} size="sm" seed={(l.actor?.email || "").length} />
                  <div>
                    <div style={{ fontWeight: 500 }}>{l.actor?.name || "—"}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{l.actor?.email}</div>
                  </div>
                </div>
              </MetaValue>

              <MetaLabel>Target</MetaLabel>
              <MetaValue>
                <span className="badge badge-muted" style={{ marginRight: 6 }}>{l.targetType}</span>
                <span style={{ fontFamily: "monospace", fontSize: 12, color: "var(--text-muted)" }}>{l.targetId}</span>
              </MetaValue>

              <MetaLabel>IP Address</MetaLabel>
              <MetaValue><IpCell ip={l.ip} /></MetaValue>
            </div>
          </div>

          {l.userAgent && (
            <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--border)" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>User Agent</div>
              <div style={{ fontSize: 12, color: "var(--text)", wordBreak: "break-all" }}>{l.userAgent}</div>
            </div>
          )}

          {metaEntries.length > 0 && (
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Metadata</div>
              <div style={{
                background: "var(--surface-2, rgba(148,163,184,0.06))",
                borderRadius: 8,
                border: "1px solid var(--border)",
                overflow: "hidden",
              }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <tbody>
                    {metaEntries.map(([key, val], idx) => (
                      <tr key={key} style={{ borderBottom: idx < metaEntries.length - 1 ? "1px solid var(--border)" : "none" }}>
                        <td style={{
                          padding: "8px 12px",
                          fontWeight: 600,
                          color: "var(--text-muted)",
                          whiteSpace: "nowrap",
                          background: "var(--surface-3, rgba(0,0,0,0.02))",
                          width: 140,
                          verticalAlign: "top",
                          fontFamily: "monospace",
                          fontSize: 11,
                        }}>
                          {key}
                        </td>
                        <td style={{ padding: "8px 12px", color: "var(--text)", wordBreak: "break-word", fontFamily: typeof val === "string" && val.length > 60 ? "monospace" : "inherit", fontSize: typeof val === "string" && val.length > 60 ? 11 : 13 }}>
                          {renderMetaValue(val)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Close</button>
        </div>
      </>,
      "lg",
    );
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Audit Log</h1>
          <p className="page-sub">
            Every admin action, recorded · {resp.total} entries
          </p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14, padding: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <Icon
                name="search" size={14}
                style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }}
              />
              <input
                className="input"
                placeholder="Search action, target, actor email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: 30, height: 34, fontSize: 13 }}
              />
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {RANGES.map(([k, l]) => (
                <button
                  key={k}
                  className={`filter-chip ${range === k && !dateFrom && !dateTo ? "active" : ""}`}
                  onClick={() => { setRange(k); setDateFrom(""); setDateTo(""); }}
                  style={{ fontSize: 12, padding: "5px 10px" }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>Actor</span>
              <select
                className="input"
                value={actor}
                onChange={(e) => setActor(e.target.value)}
                style={{ height: 30, fontSize: 12, padding: "0 6px", width: 130 }}
              >
                <option value="">All</option>
                {actors.map((a) => (
                  <option key={a.id} value={a.id}>{a.email}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>Target</span>
              <select
                className="input"
                value={targetType}
                onChange={(e) => setTargetType(e.target.value)}
                style={{ height: 30, fontSize: 12, padding: "0 6px", width: 110 }}
              >
                <option value="">All</option>
                <option value="company">company</option>
                <option value="subscription">subscription</option>
                <option value="invoice">invoice</option>
                <option value="user">user</option>
                <option value="plan">plan</option>
                <option value="support_ticket">support_ticket</option>
                <option value="webhook">webhook</option>
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>From</span>
              <input type="date" value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); }}
                style={{ height: 30, fontSize: 12, padding: "0 6px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", width: 130 }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>To</span>
              <input type="date" value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); }}
                style={{ height: 30, fontSize: 12, padding: "0 6px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", width: 130 }} />
            </div>
            {(dateFrom || dateTo) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setDateFrom(""); setDateTo(""); setRange(30); }} style={{ height: 30 }}>
                <Icon name="x" size={12} /> Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <TableSkeleton columns={6} rows={6} />
      ) : filtered.length === 0 ? (
        <EmptyState title="No log entries" desc="No actions in this range." icon="file-text" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>When</th>
                <th>Actor</th>
                <th style={{ width: 100 }}>Action</th>
                <th style={{ width: 100 }}>Target</th>
                <th>Target ID</th>
                <th style={{ width: 120 }}>IP</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} onClick={() => openMetadata(l)} style={{ cursor: "pointer" }}>
                  <td data-label="When" style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                    <span title={fmtDateTime(l.createdAt)}>{fmtRelative(l.createdAt)}</span>
                  </td>
                  <td data-label="Actor">
                    <div className="cell-emp">
                      <Avatar name={l.actor?.email || "?"} size="sm" seed={(l.actor?.email || "").length} />
                      <div>
                        <div className="name">{l.actor?.name || "—"}</div>
                        <div className="sub">{l.actor?.email}</div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Action">
                    <span className={`badge ${actionColor(l.action)}`} style={{ fontSize: 11 }}>{l.action}</span>
                  </td>
                  <td data-label="Target">
                    <span className="badge badge-muted" style={{ fontSize: 11 }}>{l.targetType}</span>
                  </td>
                  <td data-label="Target ID" style={{ fontFamily: "monospace", fontSize: 12, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {l.targetId || "—"}
                  </td>
                  <td data-label="IP">
                    <IpCell ip={l.ip} />
                  </td>
                  <td>
                    <Icon name="chevron-right" size={14} style={{ color: "var(--text-muted)" }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && resp.total > 0 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 16, alignItems: "center" }}>
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <Icon name="chevron-left" size={14} /> Prev
          </button>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Page {page} of {totalPages}
          </span>
          <button className="btn btn-ghost btn-sm" disabled={!resp.hasMore} onClick={() => setPage((p) => p + 1)}>
            Next <Icon name="chevron-right" size={14} />
          </button>
        </div>
      )}
    </section>
  );
}

function MetaLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
      {children}
    </div>
  );
}

function MetaValue({ children }) {
  return <div>{children}</div>;
}

function renderMetaValue(val) {
  if (val === null || val === undefined) return <span style={{ color: "var(--text-muted)" }}>—</span>;
  if (typeof val === "boolean") return <span className={`badge ${val ? "badge-success" : "badge-muted"}`} style={{ fontSize: 11 }}>{String(val)}</span>;
  if (typeof val === "object") {
    const str = JSON.stringify(val, null, 2);
    return <pre style={{ margin: 0, fontSize: 11, lineHeight: 1.4, color: "var(--text-muted)", maxHeight: 200, overflow: "auto", whiteSpace: "pre-wrap" }}>{str}</pre>;
  }
  if (typeof val === "string" && val.length > 80) {
    return <pre style={{ margin: 0, fontSize: 11, lineHeight: 1.4, color: "var(--text)", maxHeight: 200, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{val}</pre>;
  }
  return String(val);
}

export default function AdminAuditLog() {
  return (
    <AdminPageBoundary>
      <AuditInner />
    </AdminPageBoundary>
  );
}
